import React, { memo } from 'react';
import { TopPicks4U } from '@browsing/top-picks-4U';
import { ContinueShoppingCarousel } from '@client/components/continue-shopping';
import { CampaignBlock, Slot, DeclarativeGrid } from '../components';
import { SemanticArticleCarousel } from '../../../semantic-article-carousel';
import { NewDealsCarousel } from '../../../new-deals-today';
import { useGridLayoutContext } from '../context/GridLayoutContext';

/**
 * TwoColumnLayout - Tablet layout for the campaign grid (2 columns).
 *
 * IMPORTANT: All non-CampaignBlock components (carousels, widgets, etc.) MUST be wrapped
 * with ShowstopperBlock to prevent position changes when components are conditionally hidden.
 *
 * Why ShowstopperBlock is Required:
 * - When a carousel is hidden (e.g., condition={false}), it renders CampaignBlock(s) as fallback
 * - This prevents other carousels from shifting positions
 * - Maintains consistent layout and campaign positioning
 *
 * Usage Pattern:
 *   ✅ CORRECT:
 *   <ShowstopperBlock condition={true} fallbackLayout={[Slot.HORIZONTAL_RECTANGLE]}>
 *     <SemanticArticleCarousel />
 *   </ShowstopperBlock>
 *
 *   ❌ INCORRECT:
 *   <SemanticArticleCarousel />  // This will cause position shifts when hidden
 *
 * When Adding New Components:
 *   1. Wrap ALL carousels and widgets with ShowstopperBlock
 *   2. Use condition={true} for now (adjust conditions later as needed)
 *   3. Use fallbackLayout={[Slot.HORIZONTAL_RECTANGLE]} for two-column layout
 *   4. Have code review verify ShowstopperBlock usage
 */
const TwoColumnLayoutComponent: React.FC = () => {
    const { isSemanticArticleCarousel, topPicks4UGenderData, layoutConfig, isContinueShopping } =
        useGridLayoutContext();
    const { gender, profileFashionPreference, selectedFashionPreference } = topPicks4UGenderData;
    const { newDealPlacement } = layoutConfig;

    return (
        <DeclarativeGrid>
            {/* First 30 campaigns section with showstoppers */}
            <CampaignBlock slots={[Slot.HORIZONTAL_RECTANGLE]} />
            <CampaignBlock slots={[Slot.SQUARE, Slot.SQUARE]} />
            {/* Wrapping condition required: SemanticArticleCarousel and TopPicks4U share position 1.
            This prevents an extra campaign from appearing before TopPicks4U when SemanticArticleCarousel is missing. */}
            {isSemanticArticleCarousel && <SemanticArticleCarousel />}
            <TopPicks4U
                gender={gender}
                profileFashionPreference={profileFashionPreference}
                selectedFashionPreference={selectedFashionPreference}
            />
            {newDealPlacement === 'afterTopPicks' ? (
                <NewDealsCarousel />
            ) : (
                <CampaignBlock slots={[Slot.HORIZONTAL_RECTANGLE]} />
            )}
            <CampaignBlock slots={[Slot.HORIZONTAL_RECTANGLE]} />
            {newDealPlacement === 'afterSecondBlock' ? (
                <NewDealsCarousel />
            ) : (
                <CampaignBlock slots={[Slot.HORIZONTAL_RECTANGLE]} />
            )}
            <CampaignBlock slots={[Slot.SQUARE, Slot.SQUARE]} />
            {isContinueShopping && <ContinueShoppingCarousel />}
            <CampaignBlock slots={[Slot.HORIZONTAL_RECTANGLE]} />
            <CampaignBlock slots={[Slot.SQUARE, Slot.SQUARE]} />
            {/* Extended content - more campaign blocks following the same pattern */}
            {Array.from({ length: 25 }, (_, i) => [
                <CampaignBlock key={`extended-${i}-h`} slots={[Slot.HORIZONTAL_RECTANGLE]} />,
                <CampaignBlock key={`extended-${i}-s`} slots={[Slot.SQUARE, Slot.SQUARE]} />,
            ]).flat()}
        </DeclarativeGrid>
    );
};

export const TwoColumnLayout = memo(TwoColumnLayoutComponent);
